import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { invitationApi } from 'src/api/invitation';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { translateKey } from 'src/locales/translate';


interface NotificationsDeclineModalProps {
  fetchInvites: () => void;
  invitationToken: string;
}

export default function NotificationsDeclineModal({
  fetchInvites,
  invitationToken,
}: NotificationsDeclineModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: declineInvitation } = useMutation(
    ['organization-invite-decline', invitationToken],
    () =>
      invitationApi.invitationControllerDeclineInvitation({
        processInvitationDto: { token: invitationToken },
      }),
    {
      retry: 2,
    },
  );

  const closeModal = () => setIsOpen(false);

  const openModal = () => setIsOpen(true);

  const handleAction = async () => {
    setIsLoading(true);
    try {
      await declineInvitation();
      closeModal();
      enqueueSnackbar(translateKey("hardcoded.zaproszenie_zostalo_odrzucone"), {
        variant: 'success',
      });
      fetchInvites();
    } catch (error) {
      enqueueSnackbar(error.message || error, {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Tooltip title={translateKey("visit.rejectAction")} placement="top" arrow>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            openModal();
          }}
          color="error"
        >
          <Iconify icon="ic:round-remove-circle-outline" />
        </IconButton>
      </Tooltip>
      <Dialog open={isOpen} onClose={closeModal}>
        <DialogTitle>{translateKey("hardcoded.odrzucic_zaproszenie")}</DialogTitle>

        <DialogContent sx={{ maxWidth: '400px' }}>
          <DialogContentText id="alert-dialog-description">{translateKey("hardcoded.czy_na_pewno_chcesz_odrzucic_zaproszenie_do_placowki")}</DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={closeModal}>{translateKey("common.cancel")}</Button>
          <LoadingButton
            variant="contained"
            onClick={handleAction}
            loading={isLoading}
            color="error"
          >
            <span>{translateKey("visit.rejectAction")}</span>
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
